/**
 * @file UploadAutomationConfig.jsx
 * @description Upload automation page — lists the file drops that feed
 *              campaigns, in the order they land, and hosts the add/edit
 *              dialog. Day and window are read in the client time zone set
 *              in Configuration → Time Settings.
 * @component UploadAutomationConfig
 * @exports UploadAutomationConfig
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Alert, Box, CircularProgress, IconButton, MenuItem, Select, Snackbar,
    Switch, Tooltip, Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HistoryIcon from "@mui/icons-material/History";

import {
    AUTOMATION_STRINGS, LANGUAGE_LABEL, LANGUAGE_OPTIONS, NEW_CONFIG,
    ROWS_PER_PAGE, DEFAULT_TIME_ZONE_KEY, DAY_OF_WEEK_OPTIONS,
    describeDays, describeWindow, resolveTimeZone, clockInZone,
} from "../../common/constants/uploadAutomationModule/uploadAutomationConstants.js";
import {
    fetchAutomationConfigs, fetchAutomationCampaigns, fetchClientTimeZone,
    saveAutomationConfig, toggleAutomationConfig, deleteAutomationConfig,
} from "../../services/uploadAutomationModule/uploadAutomationServices.js";
import UploadAutomationDialog from "./UploadAutomationDialog.jsx";
import UploadAutomationFiles from "./UploadAutomationFiles.jsx";
import { nextFileName, nextRunAt } from "./fileNamePattern.js";

// ── Palette (matches the dashboard modules) ────────────────────────
const BLUE  = "#378ADD";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";

/** The API returns { error } on 400/409 — prefer it over the generic copy. */
const messageFrom = (e, fallback) =>
    e?.response?.data?.error || e?.response?.data?.message || fallback;

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    // Order leads: the whole point of the page is the sequence files arrive in.
    { key: "uploadOrder",   label: "Order",     width: 70,  align: "left"  },
    { key: "name",          label: "Automation", width: 250, align: "left" },
    { key: "language",      label: "Language",  width: 90,  align: "left"  },
    { key: "campaignName",  label: "Campaign",  width: 210, align: "left"  },
    { key: "days",          label: "Days",      width: 130, align: "left"  },
    { key: "window",        label: "Window",    width: 150, align: "left"  },
    { key: "scheduleIds",   label: "Schedules", width: 110, align: "left"  },
    { key: "nextFile",      label: "Next file", width: 240, align: "left"  },
    { key: "lastRunDate",   label: "Last run",  width: 130, align: "right" },
    { key: "isEnabled",     label: "Active",    width: 70,  align: "left"  },
];

// 96px action column at the start (edit / history / delete)
const TABLE_COLS  = [{ key: "_actions", label: "", width: 96, align: "left" }, ...COLS];
const GRID_COLS   = TABLE_COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 32;

// ── Helpers ────────────────────────────────────────────────────────
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const RUN_DATE_FMT = { weekday: "short", month: "short", day: "numeric" };

const formatDateTime = (val) => {
    const d = val ? new Date(val) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()} ${hh}:${mi}`;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

function Pill({ label, color, onClick, disabled, filled, icon }) {
    const inactive = !!disabled;
    return (
        <Box
            onClick={inactive ? undefined : onClick}
            sx={{
                px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                userSelect: "none", fontWeight: 500,
                display: "inline-flex", alignItems: "center", gap: "6px",
                border: filled ? "none" : "0.5px solid",
                borderColor: inactive ? "divider" : color,
                background: filled ? color : "transparent",
                color: filled ? "#fff" : (inactive ? "text.disabled" : color),
                cursor: inactive ? "default" : "pointer",
                opacity: inactive ? 0.5 : 1,
                transition: "all .15s",
                "&:hover": inactive ? {} : {
                    background: filled ? "#2a6db5" : "rgba(55,138,221,0.06)",
                },
            }}
        >
            {icon}{label}
        </Box>
    );
}

/** Standing reminder of which clock the upload windows are read against. */
function TimeZoneBadge({ tzKey }) {
    const tz = resolveTimeZone(tzKey);
    const [now, setNow] = useState(() => clockInZone(tz.iana));

    useEffect(() => {
        setNow(clockInZone(tz.iana));
        const id = setInterval(() => setNow(clockInZone(tz.iana)), 30000);
        return () => clearInterval(id);
    }, [tz.iana]);

    return (
        <Tooltip title={AUTOMATION_STRINGS.TZ_SOURCE} arrow>
            <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "20px",
                border: "0.5px solid", borderColor: "divider",
                background: "#f7f7f7",
                fontSize: 12, color: "text.secondary", cursor: "default",
            }}>
                <ScheduleIcon sx={{ fontSize: 14, color: BLUE }} />
                <Box component="span">
                    Windows read in{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
                        {tz.name} ({tz.key})
                    </Box>
                </Box>
                {now && (
                    <Box component="span" sx={{ color: "text.disabled" }}>· {now} there now</Box>
                )}
            </Box>
        </Tooltip>
    );
}

/** Squared-off tag, same as the report module's PDF / CSV markers. */
function Tag({ label, color }) {
    return (
        <Box component="span" sx={{
            fontSize: 10, fontWeight: 500, letterSpacing: "0.04em",
            textTransform: "uppercase",
            px: "6px", py: "2px",
            borderRadius: "4px",
            color,
            border: "0.5px solid",
            borderColor: color,
            background: "transparent",
            whiteSpace: "nowrap",
        }}>
            {label}
        </Box>
    );
}

/** The drop slot, sized to read as a sequence marker rather than an id. */
function OrderBadge({ order }) {
    return (
        <Box sx={{
            width: 26, height: 26, borderRadius: "50%",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            border: "0.5px solid", borderColor: BLUE, color: BLUE,
            fontSize: 12, fontWeight: 500,
        }}>
            {order}
        </Box>
    );
}

/** Name on top, description underneath, both clamped to one line. */
function NameCell({ name, description }) {
    return (
        <Box sx={{ minWidth: 0, py: "2px" }}>
            <Box sx={{
                fontSize: 12, fontWeight: 500, color: "text.primary",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {name || "—"}
            </Box>
            {description ? (
                <Tooltip title={description} arrow placement="bottom-start">
                    <Box sx={{
                        fontSize: 11, color: "text.secondary", mt: "1px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        cursor: "default",
                    }}>
                        {description}
                    </Box>
                </Tooltip>
            ) : null}
        </Box>
    );
}

/**
 * What the next run is waiting for. Recomputed from the stored template, so
 * it always reflects the day/window settings rather than a cached string.
 */
function NextFileCell({ row }) {
    const when = nextRunAt(row);
    const name = nextFileName(row);
    if (!name) return <Box component="span" sx={{ color: "text.disabled" }}>—</Box>;
    return (
        <Tooltip
            title={when ? `Expected ${when.toLocaleDateString(undefined, RUN_DATE_FMT)}` : "No day selected"}
            arrow
        >
            <Box component="span" sx={{
                fontFamily: MONO, fontSize: 11,
                color: row.isEnabled ? "text.primary" : "text.disabled",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                cursor: "default",
            }}>
                {name}
            </Box>
        </Tooltip>
    );
}

/** Count inline, names in the tooltip — the list can run long. */
function ScheduleCountCell({ schedules = [] }) {
    if (!schedules.length) {
        return <Box component="span" sx={{ color: AMBER }}>None</Box>;
    }
    return (
        <Tooltip title={schedules.map(s => s.scheduleName).join(", ")} arrow>
            <Box component="span" sx={{ color: BLUE, fontWeight: 500, cursor: "default" }}>
                {schedules.length} schedule{schedules.length === 1 ? "" : "s"}
            </Box>
        </Tooltip>
    );
}

// ── Component ──────────────────────────────────────────────────────
const UploadAutomationConfig = () => {
    const [configs,   setConfigs]   = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [tzKey,     setTzKey]     = useState(DEFAULT_TIME_ZONE_KEY);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState("");
    const [toast,     setToast]     = useState("");
    const [langFilter, setLangFilter] = useState("all");
    const [page,      setPage]      = useState(0);
    const [sortKey,   setSortKey]   = useState("uploadOrder");
    const [sortDir,   setSortDir]   = useState("asc");
    const [editing,   setEditing]   = useState(null);   // config object or null
    const [saving,    setSaving]    = useState(false);
    const [busyId,    setBusyId]    = useState(null);   // row-level spinner
    const [history,   setHistory]   = useState(null);   // config whose files are open

    const load = useCallback(async () => {
        try {
            setLoading(true); setError(""); setPage(0);
            const [list, camps, tz] = await Promise.all([
                fetchAutomationConfigs(),
                fetchAutomationCampaigns(),
                fetchClientTimeZone(),
            ]);
            setConfigs(list || []);
            setCampaigns(camps || []);
            setTzKey(tz || DEFAULT_TIME_ZONE_KEY);
        } catch (e) {
            console.error(e);
            setError(messageFrom(e, AUTOMATION_STRINGS.LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Filter / sort / page ──────────────────────────────────────
    const filtered = useMemo(() => (
        langFilter === "all" ? configs : configs.filter(c => c.language === langFilter)
    ), [configs, langFilter]);

    const sorted = useMemo(() => {
        if (!sortKey) return filtered;
        const valueOf = (row) => {
            if (sortKey === "scheduleIds") return (row.schedules || []).length;
            if (sortKey === "isEnabled")   return row.isEnabled ? 1 : 0;
            if (sortKey === "days")        return (row.days || []).length;
            if (sortKey === "window")      return row.startTime || "";
            if (sortKey === "nextFile")    return nextFileName(row) || "";
            return row[sortKey];
        };
        return [...filtered].sort((a, b) => {
            const av = valueOf(a), bv = valueOf(b);
            const an = Number(av), bn = Number(bv);
            const cmp = av !== "" && bv !== "" && !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [filtered, sortKey, sortDir]);

    const paged      = useMemo(() =>
        sorted.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sorted, page]);
    const totalPages = Math.ceil(sorted.length / ROWS_PER_PAGE);

    const handleSortCol = (key) => {
        if (key === "_actions") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    // ── KPIs ──────────────────────────────────────────────────────
    //
    // Active, then one cell per weekday. The day counts are distinct
    // campaigns, not automations: two drops feeding the same campaign on a
    // Monday are one campaign being fed, which is the number worth watching.
    // Paused configs are excluded — they aren't going to feed anything.
    //
    // Schedules are counted the same way: distinct schedule ids linked to an
    // active automation, so a schedule shared by two drops counts once. A
    // linked schedule that has since been paused on the campaign side is
    // left out — it can't fire.
    const kpis = useMemo(() => {
        const byDay      = {};
        const schByDay   = {};
        const schTotal   = new Set();
        for (const d of DAY_OF_WEEK_OPTIONS) {
            byDay[d.value]    = new Set();
            schByDay[d.value] = new Set();
        }

        const activeScheduleIds = (c) => (
            c.schedules?.length
                ? c.schedules.filter(s => s.isActive !== false).map(s => s.scheduleId)
                : (c.scheduleIds || [])
        );

        for (const c of configs) {
            if (!c.isEnabled) continue;
            const ids = activeScheduleIds(c);
            ids.forEach(id => schTotal.add(id));
            for (const day of c.days || []) {
                if (!byDay[day]) continue;
                byDay[day].add(c.campaignId);
                ids.forEach(id => schByDay[day].add(id));
            }
        }

        return {
            active: configs.filter(c => c.isEnabled).length,
            schedules: schTotal.size,
            byDay: Object.fromEntries(
                DAY_OF_WEEK_OPTIONS.map(d => [d.value, byDay[d.value].size])
            ),
            schedulesByDay: Object.fromEntries(
                DAY_OF_WEEK_OPTIONS.map(d => [d.value, schByDay[d.value].size])
            ),
        };
    }, [configs]);

    /**
     * Orders already claimed, keyed by day: { mon: Set(1,3), tue: Set(1), … }.
     *
     * The order only has to be unique among automations that share a day, so
     * the dialog needs it broken down that way rather than by language.
     */
    const takenOrders = useMemo(() => {
        const map = {};
        for (const c of configs) {
            if (!c.isEnabled) continue;
            for (const day of c.days || []) {
                if (!map[day]) map[day] = new Set();
                map[day].add(c.uploadOrder);
            }
        }
        return map;
    }, [configs]);

    // ── Actions ───────────────────────────────────────────────────
    const handleSave = async (draft) => {
        try {
            setSaving(true); setError("");
            const saved = await saveAutomationConfig(draft);

            setConfigs(prev => {
                if (!saved) return prev;
                return draft.configId
                    ? prev.map(c => c.configId === saved.configId ? saved : c)
                    : [saved, ...prev];
            });
            if (!saved) await load();          // backend returned no body — refetch
            setEditing(null);
            setToast(draft.configId ? "Automation updated" : "Automation created");
        } catch (e) {
            console.error(e);
            setError(messageFrom(e, AUTOMATION_STRINGS.SAVE_FAILED));
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (row) => {
        const next = !row.isEnabled;
        setConfigs(prev => prev.map(c =>                    // optimistic
            c.configId === row.configId ? { ...c, isEnabled: next } : c));
        try {
            await toggleAutomationConfig(row.configId, next);
        } catch (e) {
            console.error(e);
            setConfigs(prev => prev.map(c =>                // roll back
                c.configId === row.configId ? { ...c, isEnabled: !next } : c));
            setError(messageFrom(e, AUTOMATION_STRINGS.SAVE_FAILED));
        }
    };

    const handleDelete = async (row) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(AUTOMATION_STRINGS.DELETE_CONFIRM)) return;
        try {
            setBusyId(row.configId); setError("");
            await deleteAutomationConfig(row.configId);
            setConfigs(prev => prev.filter(c => c.configId !== row.configId));
            setToast("Automation deleted");
        } catch (e) {
            console.error(e);
            setError(messageFrom(e, AUTOMATION_STRINGS.DELETE_FAILED));
        } finally {
            setBusyId(null);
        }
    };

    const tz = resolveTimeZone(tzKey);

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
        columnGap: "8px",
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Toolbar ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                        Language
                    </Typography>
                    <Select
                        size="small"
                        value={langFilter}
                        onChange={(e) => { setLangFilter(e.target.value); setPage(0); }}
                        sx={{ minWidth: 180, fontSize: 13, borderRadius: "20px" }}
                    >
                        <MenuItem value="all" sx={{ fontSize: 13 }}>All languages</MenuItem>
                        {LANGUAGE_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Pill
                    label={AUTOMATION_STRINGS.ADD_CONFIG}
                    color={BLUE}
                    filled
                    disabled={loading || campaigns.length === 0}
                    onClick={() => setEditing({ ...NEW_CONFIG })}
                />
                <Pill label="Refresh" color={BLUE} disabled={loading} onClick={load} />

                <Box sx={{ ml: "auto", alignSelf: "flex-end" }}>
                    <TimeZoneBadge tzKey={tzKey} />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && configs.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {AUTOMATION_STRINGS.NO_CONFIGS}
                </Typography>
            )}

            {!loading && configs.length > 0 && (
                <>
                    {/* ── Summary ── */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "stretch" }}>
                        {/* Active leads and gets the extra width; the seven day
                            cells share what's left so the row stays one line. */}
                        <Box sx={{
                            background: "#f7f7f7", borderRadius: 2,
                            p: "10px 14px", flex: "1.6 1 0", minWidth: 0,
                        }}>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                Active
                            </Typography>
                            <Typography sx={{ fontSize: 20, fontWeight: 500, color: GREEN, lineHeight: 1.2 }}>
                                {kpis.active.toLocaleString()}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                                {kpis.schedules.toLocaleString()} schedule{kpis.schedules === 1 ? "" : "s"}
                            </Typography>
                        </Box>

                        {DAY_OF_WEEK_OPTIONS.map(d => {
                            const n = kpis.byDay[d.value] || 0;
                            const s = kpis.schedulesByDay[d.value] || 0;
                            return (
                                <Tooltip
                                    key={d.value}
                                    title={`${n} campaign${n === 1 ? "" : "s"} · ${s} schedule${s === 1 ? "" : "s"} on ${d.full}`}
                                    arrow
                                >
                                    <Box sx={{
                                        background: "#f7f7f7", borderRadius: 2,
                                        p: "10px 12px", flex: "1 1 0", minWidth: 0,
                                        cursor: "default",
                                    }}>
                                        <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                            {d.short}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: 20, fontWeight: 500, lineHeight: 1.2,
                                            color: n ? BLUE : "text.disabled",
                                        }}>
                                            {n}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: 11, mt: 0.25,
                                            color: s ? "text.secondary" : "text.disabled",
                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                        }}>
                                            {s} sch
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {/* ── Table ── */}
                    <SectionTitle>Automations</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {TABLE_COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    title={col.key === "window" ? `${tz.name} (${tz.key})` : undefined}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: col.key === "_actions" ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": col.key === "_actions" ? {} : { color: BLUE },
                                    }}
                                >
                                    {col.key === "window" ? `Window (${tz.key})` : col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Rows */}
                        {paged.map((row, i) => (
                            <Box key={row.configId ?? i} sx={{
                                ...gridSx, py: "7px", fontSize: 12,
                                borderBottom: "0.5px solid", borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                opacity: row.isEnabled ? 1 : 0.6,
                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                                alignItems: "center",
                            }}>
                                {/* Actions */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {busyId === row.configId ? (
                                        <CircularProgress size={16} sx={{ ml: "6px" }} />
                                    ) : (
                                        <>
                                            <Tooltip title="Edit automation" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => setEditing({ ...row })}>
                                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="File history" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => setHistory(row)}>
                                                    <HistoryIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete automation" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => handleDelete(row)}>
                                                    <DeleteOutlineIcon sx={{ fontSize: 15, color: PINK }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>

                                {COLS.map(col => (
                                    <Box key={col.key} sx={{
                                        textAlign: col.align,
                                        display: "flex", alignItems: "center", minWidth: 0,
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        ...(col.key === "campaignName" && { color: BLUE }),
                                        ...(col.key === "lastRunDate" && { color: "text.secondary" }),
                                    }}>
                                        {col.key === "uploadOrder" ? <OrderBadge order={row.uploadOrder} />
                                            : col.key === "name" ? (
                                                <NameCell name={row.name} description={row.description} />
                                            )
                                            : col.key === "language" ? (
                                                <Tag
                                                    label={row.language}
                                                    color={row.language === "SP" ? AMBER : BLUE}
                                                />
                                            )
                                            : col.key === "days" ? (
                                                <Tooltip title={(row.days || []).join(", ")} arrow>
                                                    <Box component="span" sx={{ whiteSpace: "nowrap", cursor: "default" }}>
                                                        {describeDays(row.days)}
                                                    </Box>
                                                </Tooltip>
                                            )
                                            : col.key === "window" ? (
                                                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                                                    {describeWindow(row.startTime, row.endTime)}
                                                </Box>
                                            )
                                            : col.key === "scheduleIds" ? (
                                                <ScheduleCountCell schedules={row.schedules} />
                                            )
                                            : col.key === "nextFile" ? (
                                                <NextFileCell row={row} />
                                            )
                                            : col.key === "lastRunDate" ? (
                                                <Tooltip title={row.lastFileName || row.lastRunStatus || ""} arrow>
                                                    <Box component="span" sx={{
                                                        whiteSpace: "nowrap",
                                                        color: row.lastRunStatus === "failed" ? PINK : "inherit",
                                                    }}>
                                                        {formatDateTime(row.lastRunDate)}
                                                    </Box>
                                                </Tooltip>
                                            )
                                            : col.key === "isEnabled" ? (
                                                <Switch
                                                    size="small"
                                                    checked={!!row.isEnabled}
                                                    onChange={() => handleToggle(row)}
                                                />
                                            )
                                            : (
                                                <Box component="span" sx={{
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                }}>
                                                    {row[col.key] || "—"}
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", mt: 1, px: 1,
                        }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, sorted.length)} of {sorted.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Add / edit dialog */}
            {editing && (
                <UploadAutomationDialog
                    open={!!editing}
                    config={editing}
                    tzKey={tzKey}
                    saving={saving}
                    takenOrders={takenOrders}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                />
            )}

            {history && (
                <UploadAutomationFiles
                    open={!!history}
                    config={history}
                    onClose={() => setHistory(null)}
                />
            )}

            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast("")}
                message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box>
    );
};

export default UploadAutomationConfig;